/* Protected preview maps only. Full sources and download URLs never belong in this contract.
   Supports a single map set, or {selected_id, members:[{id,name,poster,facts,...mapSet}]}.
   Three and texture requests start only after Explore in 3D. */
(() => {
  const THREE_URL = 'https://cdn.jsdelivr.net/npm/three@0.170.0/+esm';
  const ADDONS = 'https://cdn.jsdelivr.net/npm/three@0.170.0/examples/jsm/';
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const mapNames = {basecolor:'map', normal:'normalMap', roughness:'roughnessMap', ao:'aoMap', metallic:'metalnessMap'};
  const webp = value => { try { return typeof value === 'string' && /\.webp(?:\?|$)/i.test(value) && ['http:', 'https:'].includes(new URL(value, location.href).protocol); } catch { return false; } };

  function init(root) {
    if (root.dataset.mvInitialized) return;
    root.dataset.mvInitialized = 'true';
    const stage = root.querySelector('[data-mv-stage]');
    const open = root.querySelector('[data-mv-open]');
    const fields = root.querySelector('.mv__controls');
    const picker = root.querySelector('[data-mv-member]');
    let members, selected, runtime, creating, busy = false, opened = false, generation = 0;
    try {
      const data = JSON.parse(root.dataset.material);
      members = data.members || [{...data, id:data.id || 'single', name:data.name || data.preview_material_name}];
      if (!members.length || new Set(members.map(m => m.id)).size !== members.length) throw Error('Invalid members');
      selected = members.find(m => m.id === data.selected_id) || members[0];
    } catch (e) { root.classList.add('is-failed'); open.disabled = true; return; }

    function facts(member) {
      if (member.name) root.querySelector('[data-mv-name]').textContent = member.name;
      if (picker) picker.value = member.id;
      if (member.facts) root.querySelectorAll('[data-mv-fact]').forEach(node => {
        const key = node.dataset.mvFact;
        let value = member.facts[key];
        if (key === 'seamless') value = value === true ? 'Seamless, checked tiled' : '';
        if (Array.isArray(value)) value = value.join(key === 'resolution' ? ' × ' : ', ');
        node.textContent = value || '';
        node.parentElement.hidden = !value;
      });
      const poster = stage.querySelector('.mv__poster');
      if (poster && webp(member.poster)) {
        poster.removeAttribute('srcset'); poster.removeAttribute('sizes');
        poster.src = member.poster; poster.alt = `${member.name} on demonstration geometry`;
      } else if (poster && members.length > 1) poster.hidden = true;
      if (poster && webp(member.poster)) poster.hidden = false;
      root.dataset.mvSelected = member.id;
    }
    facts(selected);

    async function create() {
      const THREE = await import(THREE_URL);
      const [{OrbitControls}, {RoomEnvironment}, {RoundedBoxGeometry}] = await Promise.all([
        'controls/OrbitControls.js', 'environments/RoomEnvironment.js', 'geometries/RoundedBoxGeometry.js'
      ].map(path => import(ADDONS + path + '/+esm')));
      const renderer = new THREE.WebGLRenderer({antialias:true});
      renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
      renderer.toneMapping = THREE.AgXToneMapping;
      renderer.outputColorSpace = THREE.SRGBColorSpace;
      renderer.domElement.setAttribute('role', 'img');
      stage.appendChild(renderer.domElement);
      const scene = new THREE.Scene(); scene.background = new THREE.Color(0x111113);
      const pmrem = new THREE.PMREMGenerator(renderer), room = new RoomEnvironment();
      const environment = pmrem.fromScene(room, 0.04);
      scene.environment = environment.texture; scene.environmentIntensity = 0.55;
      room.dispose(); pmrem.dispose();
      const camera = new THREE.PerspectiveCamera(32,1,0.01,50); camera.position.set(0.9,0.5,1.5);
      const controls = new OrbitControls(camera,renderer.domElement);
      controls.enableDamping = !reduced.matches; controls.enablePan = false;
      controls.minDistance = 0.7; controls.maxDistance = 3; controls.autoRotate = false; controls.autoRotateSpeed = 0.6;
      const key = new THREE.DirectionalLight(0xfff2e2,2.6), rim = new THREE.DirectionalLight(0xdfe8ff,0.8);
      rim.position.set(1.4,0.8,-1.6); scene.add(key,rim);
      const shapes = {
        sphere:{geo:new THREE.SphereGeometry(0.3,96,64),span:[Math.PI*0.6,Math.PI*0.3]},
        cube:{geo:new RoundedBoxGeometry(0.5,0.5,0.5,6,0.03),span:[0.5,0.5]},
        plane:{geo:new THREE.PlaneGeometry(1.2,1.2),span:[1.2,1.2]}
      };
      let shape = 'sphere', maps = {}, material = new THREE.MeshStandardMaterial(), tile = [1,1];
      const mesh = new THREE.Mesh(shapes.sphere.geo, material); scene.add(mesh);
      const scale = root.querySelector('[data-mv-scale]'), light = root.querySelector('[data-mv-light]');
      const rotate = root.querySelector('[data-mv-rotate]');
      const normal = root.querySelector('[data-mv-normal]'), roughness = root.querySelector('[data-mv-roughness]'), reset = root.querySelector('[data-mv-reset]');
      const surface = () => { material.normalScale.setScalar(Number(normal.value)); material.roughness = Number(roughness.value) * (maps.roughnessMap ? 1 : (selected.roughness_value ?? 0.8));
        for (const [name,input] of [['normal',normal],['roughness',roughness],['scale',scale]]) root.querySelector(`[data-mv-${name}-value]`).textContent=input.value+'×';
        root.dataset.mvNormal=normal.value;root.dataset.mvRoughness=roughness.value; };
      normal.addEventListener('input',surface); roughness.addEventListener('input',surface); scale.addEventListener('input',surface);
      const repeat = () => Object.values(maps).forEach(t => t.repeat.set(shapes[shape].span[0]/tile[0]*Number(scale.value),shapes[shape].span[1]/tile[1]*Number(scale.value)));
      const shapeClick = e => {
        const name = e.currentTarget.dataset.mvShape; shape = name; mesh.geometry = shapes[name].geo;
        mesh.rotation.set(name === 'plane' ? -Math.PI/2.6 : 0,0,0); repeat();
        root.querySelectorAll('[data-mv-shape]').forEach(b => b.setAttribute('aria-pressed',String(b.dataset.mvShape === name)));
        root.dataset.mvShape = name;
      };
      root.querySelectorAll('[data-mv-shape]').forEach(b => b.addEventListener('click',shapeClick));
      scale.addEventListener('input',repeat);
      const lighting = () => { const a=Number(light.value)*Math.PI/180; key.position.set(Math.cos(a)*2,1.4,Math.sin(a)*2); };
      light.addEventListener('input',lighting); lighting();
      const rotating = () => { controls.autoRotate = !controls.autoRotate; rotate.setAttribute('aria-pressed',String(controls.autoRotate)); };
      rotate.addEventListener('click',rotating);
      const motion = () => { controls.enableDamping=!reduced.matches; if (reduced.matches) { controls.autoRotate=false; rotate.setAttribute('aria-pressed','false'); } };
      reduced.addEventListener('change',motion);
      const defaults = () => {shape='sphere';mesh.geometry=shapes.sphere.geo;mesh.rotation.set(0,0,0);root.dataset.mvShape='sphere';root.querySelectorAll('[data-mv-shape]').forEach(b=>b.setAttribute('aria-pressed',String(b.dataset.mvShape==='sphere')));scale.value='1';normal.value='1';roughness.value='1';light.value='140';controls.autoRotate=false;rotate.setAttribute('aria-pressed','false');camera.position.set(0.9,0.5,1.5);controls.target.set(0,0,0);repeat();lighting();surface();};
      reset.addEventListener('click',defaults);
      const contextLost = event => {event.preventDefault();generation++;runtime?.dispose();runtime=null;creating=null;busy=false;root.classList.remove('is-live','is-loading');root.classList.add('is-failed');fields.disabled=true;open.disabled=false;open.querySelector('span').textContent='Retry 3D preview';};
      renderer.domElement.addEventListener('webglcontextlost',contextLost);
      const resize = () => { if (!stage.clientWidth || !stage.clientHeight) return; renderer.setSize(stage.clientWidth,stage.clientHeight,false); camera.aspect=stage.clientWidth/stage.clientHeight; camera.updateProjectionMatrix(); };
      const observer = new ResizeObserver(resize); observer.observe(stage); resize();
      let visible=true; const intersection=new IntersectionObserver(([entry]) => { visible=entry.isIntersecting; }); intersection.observe(stage);
      renderer.setAnimationLoop(() => { if (!visible || document.hidden || !root.classList.contains('is-live')) return; controls.update(); renderer.render(scene,camera); });
      const loader = new THREE.TextureLoader();
      return {
        async update(data, token) {
          if (!webp(data.basecolor) || !Array.isArray(data.tile_m) || data.tile_m.length !== 2 || data.tile_m.some(n => !Number.isFinite(n) || n <= 0)) throw Error('Invalid protected map set');
          const pending = await Promise.allSettled(Object.entries(mapNames).filter(([key]) => data[key]).map(async ([key, slot]) => {
            if (!webp(data[key])) throw Error('Preview maps must be WebP');
            const t = await loader.loadAsync(data[key]);
            if (Math.max(t.image.width,t.image.height)>512) {t.dispose(); throw Error('Preview exceeds protected size');}
            t.wrapS=t.wrapT=THREE.RepeatWrapping; t.colorSpace=key==='basecolor'?THREE.SRGBColorSpace:THREE.NoColorSpace;
            t.anisotropy=renderer.capabilities.getMaxAnisotropy(); return [slot,t];
          }));
          const textures=pending.filter(p=>p.status==='fulfilled').map(p=>p.value);
          if (token !== generation || pending.some(p=>p.status==='rejected')) {
            textures.forEach(([,t])=>t.dispose()); if (token===generation) throw Error('Could not load protected maps'); return false;
          }
          Object.values(maps).forEach(t=>t.dispose()); material.dispose(); maps=Object.fromEntries(textures); tile=data.tile_m;
          material=new THREE.MeshStandardMaterial({...maps,roughness:maps.roughnessMap?1:(data.roughness_value??0.8),metalness:maps.metalnessMap?1:(data.metallic_value??0)});
          mesh.material=material; defaults(); renderer.domElement.setAttribute('aria-label',`${data.name || 'Material'} interactive preview`);
          root.dataset.mvLoaded=data.id; return true;
        },
        dispose() {
          renderer.domElement.removeEventListener('webglcontextlost',contextLost);normal.removeEventListener('input',surface);roughness.removeEventListener('input',surface);scale.removeEventListener('input',surface);reset.removeEventListener('click',defaults);
          renderer.setAnimationLoop(null); observer.disconnect(); intersection.disconnect(); controls.dispose(); environment.dispose(); renderer.dispose(); renderer.domElement.remove();
          Object.values(maps).forEach(t=>t.dispose()); material.dispose(); Object.values(shapes).forEach(s=>s.geo.dispose());
          root.querySelectorAll('[data-mv-shape]').forEach(b=>b.removeEventListener('click',shapeClick)); scale.removeEventListener('input',repeat); light.removeEventListener('input',lighting); rotate.removeEventListener('click',rotating); reduced.removeEventListener('change',motion);
        }
      };
    }
    async function show() {
      const focusOnReady=document.activeElement===open;
      const token = ++generation; busy=true; opened=true; open.disabled=true; fields.disabled=true;
      root.classList.remove('is-live','is-failed'); root.classList.add('is-loading'); stage.setAttribute('aria-busy','true');
      try {
        if (!runtime) {
          creating ||= create().catch(error => { creating=null; throw error; });
          runtime=await creating;
        }
        if (token!==generation) return;
        if (await runtime.update(selected,token)) {root.classList.remove('is-loading'); root.classList.add('is-live'); fields.disabled=false;if(focusOnReady)root.querySelector('[data-mv-shape]').focus();}
      } catch (error) {
        if (token!==generation) return;
        root.classList.remove('is-loading'); root.classList.add('is-failed');
        open.querySelector('span').textContent='Retry 3D preview';
      } finally { if (token===generation) {busy=false;open.disabled=false;stage.setAttribute('aria-busy','false');} }
    }
    open.addEventListener('click',()=> {if (!busy) show();});
    picker?.addEventListener('change',()=> {
      selected=members.find(m=>m.id===picker.value); facts(selected);
      if (opened) show();
    });
    root.addEventListener('shopify:section:unload',()=> {generation++;runtime?.dispose();});
  }
  document.querySelectorAll('[data-material-viewer]').forEach(init);
  document.addEventListener('shopify:section:load',e=>e.target.querySelectorAll('[data-material-viewer]').forEach(init));
})();
